import { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import {
    LogOut, Plus, Trash2, Pencil, Save, Upload, Calendar as CalIcon,
    FileText, Users, Megaphone, ClipboardList, Settings as SettingsIcon,
    ShieldCheck, X, Check, Wallet
} from "lucide-react";
import api from "@/lib/api";
import { useAdminAuth } from "@/context/AdminAuth";

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

function useUpload() {
    const inputRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const trigger = (cb) => {
        inputRef.current.onchange = async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            setBusy(true);
            try {
                const fd = new FormData();
                fd.append("file", file);
                const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                cb(data.url);
                toast.success("Uploaded");
            } catch {
                toast.error("Upload failed");
            } finally {
                setBusy(false);
                e.target.value = "";
            }
        };
        inputRef.current.click();
    };
    const el = <input ref={inputRef} type="file" accept="image/*" className="hidden" data-testid="admin-file-input" />;
    return { trigger, busy, el };
}

/* -------------------- Panels -------------------- */
function PayoutsPanel() {
    const [items, setItems] = useState([]);
    const empty = { label: "", details: "", instructions: "", active: true, order: 0 };
    const [draft, setDraft] = useState(empty);
    const [editingId, setEditingId] = useState(null);
    const load = () => api.get("/admin/payouts").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);
    const save = async () => {
        if (!draft.label.trim()) { toast.error("Label is required"); return; }
        try {
            if (editingId) await api.put(`/admin/payouts/${editingId}`, draft);
            else await api.post("/admin/payouts", draft);
            toast.success("Saved");
            setDraft(empty); setEditingId(null);
            load();
        } catch { toast.error("Save failed"); }
    };
    const del = async (id) => { await api.delete(`/admin/payouts/${id}`); load(); toast.success("Removed"); };
    return (
        <div className="grid lg:grid-cols-2 gap-6">
            <div className="card-clay p-6">
                <h3 className="font-display text-xl">{editingId ? "Edit payout method" : "Add payout method"}</h3>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Clients see these on the confirmation page after booking a paid service. Examples: Zelle, PayPal, Cash App, Bank transfer, Cash on arrival.</p>
                <div className="mt-4 space-y-3">
                    <div>
                        <label className="block text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))] font-mono">Label</label>
                        <input placeholder="e.g. Zelle" value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} className="w-full bg-[hsl(var(--input))] rounded-md px-3 py-2 text-sm mt-1" data-testid="payout-label" />
                    </div>
                    <div>
                        <label className="block text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))] font-mono">Details</label>
                        <input placeholder="Email, handle, account number, or address" value={draft.details} onChange={(e) => setDraft({ ...draft, details: e.target.value })} className="w-full bg-[hsl(var(--input))] rounded-md px-3 py-2 text-sm mt-1" data-testid="payout-details" />
                    </div>
                    <div>
                        <label className="block text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))] font-mono">Instructions (optional)</label>
                        <textarea rows={3} placeholder="e.g. Please include your name in the memo" value={draft.instructions} onChange={(e) => setDraft({ ...draft, instructions: e.target.value })} className="w-full bg-[hsl(var(--input))] rounded-md px-3 py-2 text-sm mt-1" data-testid="payout-instructions" />
                    </div>
                    <div className="flex gap-3 items-center flex-wrap">
                        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} data-testid="payout-active" /> Show to clients</label>
                        <input type="number" placeholder="Order" value={draft.order} onChange={(e) => setDraft({ ...draft, order: parseInt(e.target.value || "0") })} className="w-24 bg-[hsl(var(--input))] rounded-md px-3 py-2 text-sm" data-testid="payout-order" />
                    </div>
                    <div className="flex gap-2">
                        <button onClick={save} className="pill btn-primary px-5 py-2 text-sm" data-testid="payout-save"><Save size={14} className="inline mr-1" /> Save</button>
                        {editingId && <button onClick={() => { setDraft(empty); setEditingId(null); }} className="pill px-5 py-2 text-sm border border-[hsl(var(--border))]"><X size={14} className="inline mr-1" /> Cancel</button>}
                    </div>
                </div>
            </div>
            <div className="space-y-3">
                {items.map((p) => (
                    <div key={p.id} className="card-clay p-4" data-testid={`admin-payout-${p.id}`}>
                        <div className="flex justify-between items-start gap-3">
                            <div>
                                <div className="font-medium flex items-center gap-2">
                                    {p.label}
                                    {!p.active && <span className="text-[10px] font-mono uppercase tracking-widest bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] px-1.5 py-0.5 rounded">Hidden</span>}
                                </div>
                                {p.details && <div className="text-sm font-mono mt-1 text-[hsl(var(--primary))]">{p.details}</div>}
                                {p.instructions && <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{p.instructions}</div>}
                                <div className="text-[10px] font-mono uppercase tracking-widest text-[hsl(var(--muted-foreground))] mt-1">Order {p.order}</div>
                            </div>
                            <div className="flex gap-2">
                                <button onClick={() => { setEditingId(p.id); setDraft(p); }} className="p-2 hover:bg-[hsl(var(--secondary))] rounded" data-testid={`payout-edit-${p.id}`}><Pencil size={14} /></button>
                                <button onClick={() => del(p.id)} className="p-2 hover:bg-[hsl(var(--secondary))] rounded text-[hsl(var(--destructive))]" data-testid={`payout-del-${p.id}`}><Trash2 size={14} /></button>
                            </div>
                        </div>
                    </div>
                ))}
                {items.length === 0 && <div className="text-sm text-[hsl(var(--muted-foreground))]">No payout methods yet — clients booking paid services won't see any payment instructions until you add at least one.</div>}
            </div>
        </div>
    );
}

function ServicesPanel() {
    const [items, setItems] = useState([]);
    const empty = { title: "", description: "", duration_min: 30, is_paid: false, price: 0, currency: "USD", active: true, order: 0 };
    const [draft, setDraft] = useState(empty);
    const [editingId, setEditingId] = useState(null);

    const load = () => api.get("/admin/services").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const save = async () => {
        try {
            if (editingId) await api.put(`/admin/services/${editingId}`, draft);
            else await api.post("/admin/services", draft);
            toast.success("Saved");
            setDraft(empty); setEditingId(null);
            load();
        } catch { toast.error("Save failed"); }
    };
    const edit = (s) => { setEditingId(s.id); setDraft(s); };
    const del = async (id) => { await api.delete(`/admin/services/${id}`); load(); toast.success("Deleted"); };

    return (
        <div className="grid lg:grid-cols-2 gap-6">
            <div className="card-clay p-6">
                <h3 className="font-display text-xl">{editingId ? "Edit service" : "Add service"}</h3>
                <div className="mt-4 space-y-3">
                    <input placeholder="Title" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className="w-full bg-[hsl(var(--input))] rounded-md px-3 py-2 text-sm" data-testid="svc-title" />
                    <textarea placeholder="Description" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} className="w-full bg-[hsl(var(--input))] rounded-md px-3 py-2 text-sm" rows={3} data-testid="svc-desc" />
                    <div className="grid grid-cols-3 gap-2">
                        <input type="number" placeholder="Minutes" value={draft.duration_min} onChange={(e) => setDraft({ ...draft, duration_min: parseInt(e.target.value || "0") })} className="bg-[hsl(var(--input))] rounded-md px-3 py-2 text-sm" data-testid="svc-mins" />
                        <input type="text" inputMode="decimal" placeholder="Price" value={draft.price} onChange={(e) => { const v = e.target.value.replace(/[^0-9.]/g, ""); setDraft({ ...draft, price: v === "" ? 0 : (isNaN(parseFloat(v)) ? draft.price : parseFloat(v)) }); }} className="bg-[hsl(var(--input))] rounded-md px-3 py-2 text-sm" data-testid="svc-price" />
                        <input placeholder="Currency" value={draft.currency} onChange={(e) => setDraft({ ...draft, currency: e.target.value })} className="bg-[hsl(var(--input))] rounded-md px-3 py-2 text-sm" data-testid="svc-currency" />
                    </div>
                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={draft.is_paid} onChange={(e) => setDraft({ ...draft, is_paid: e.target.checked })} data-testid="svc-paid" /> Paid service</label>
                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} data-testid="svc-active" /> Active</label>
                    <input type="number" placeholder="Order" value={draft.order} onChange={(e) => setDraft({ ...draft, order: parseInt(e.target.value || "0") })} className="w-full bg-[hsl(var(--input))] rounded-md px-3 py-2 text-sm" data-testid="svc-order" />
                    <div className="flex gap-2">
                        <button onClick={save} className="pill btn-primary px-5 py-2 text-sm" data-testid="svc-save"><Save size={14} className="inline mr-1" /> Save</button>
                        {editingId && <button onClick={() => { setDraft(empty); setEditingId(null); }} className="pill px-5 py-2 text-sm border border-[hsl(var(--border))]"><X size={14} className="inline mr-1" /> Cancel</button>}
                    </div>
                </div>
            </div>
            <div className="space-y-3">
                {items.map((s) => (
                    <div key={s.id} className="card-clay p-4 flex items-center justify-between" data-testid={`admin-svc-${s.id}`}>
                        <div>
                            <div className="font-medium">{s.title} <span className="text-xs text-[hsl(var(--muted-foreground))]">· {s.duration_min}min</span></div>
                            <div className="text-xs text-[hsl(var(--muted-foreground))]">{s.is_paid ? `Paid $${s.price}` : "Free"} · order {s.order} · {s.active ? "active" : "hidden"}</div>
                        </div>
                        <div className="flex gap-2">
                            <button onClick={() => edit(s)} className="p-2 hover:bg-[hsl(var(--secondary))] rounded" data-testid={`svc-edit-${s.id}`}><Pencil size={14} /></button>
                            <button onClick={() => del(s.id)} className="p-2 hover:bg-[hsl(var(--secondary))] rounded text-[hsl(var(--destructive))]" data-testid={`svc-del-${s.id}`}><Trash2 size={14} /></button>
                        </div>
                    </div>
                ))}
                {items.length === 0 && <div className="text-sm text-[hsl(var(--muted-foreground))]">No services yet.</div>}
            </div>
        </div>
    );
}

function PoliciesPanel() {
    const [items, setItems] = useState([]);
    const empty = { title: "", body: "", is_paid: false, price: 0, order: 0 };
    const [draft, setDraft] = useState(empty);
    const [editingId, setEditingId] = useState(null);
    const load = () => api.get("/policies").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);
    const save = async () => {
        try {
            if (editingId) await api.put(`/admin/policies/${editingId}`, draft);
            else await api.post("/admin/policies", draft);
            toast.success("Saved");
            setDraft(empty); setEditingId(null);
            load();
        } catch { toast.error("Save failed"); }
    };
    const del = async (id) => { await api.delete(`/admin/policies/${id}`); load(); };
    return (
        <div className="grid lg:grid-cols-2 gap-6">
            <div className="card-clay p-6">
                <h3 className="font-display text-xl">{editingId ? "Edit policy" : "Add policy"}</h3>
                <div className="mt-4 space-y-3">
                    <input placeholder="Title" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className="w-full bg-[hsl(var(--input))] rounded-md px-3 py-2 text-sm" data-testid="pol-title" />
                    <textarea placeholder="Body" value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} rows={6} className="w-full bg-[hsl(var(--input))] rounded-md px-3 py-2 text-sm" data-testid="pol-body" />
                    <div className="grid grid-cols-2 gap-2">
                        <input type="text" inputMode="decimal" placeholder="Price" value={draft.price} onChange={(e) => { const v = e.target.value.replace(/[^0-9.]/g, ""); setDraft({ ...draft, price: v === "" ? 0 : (isNaN(parseFloat(v)) ? draft.price : parseFloat(v)) }); }} className="bg-[hsl(var(--input))] rounded-md px-3 py-2 text-sm" data-testid="pol-price" />
                        <input type="number" placeholder="Order" value={draft.order} onChange={(e) => setDraft({ ...draft, order: parseInt(e.target.value || "0") })} className="bg-[hsl(var(--input))] rounded-md px-3 py-2 text-sm" data-testid="pol-order" />
                    </div>
                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={draft.is_paid} onChange={(e) => setDraft({ ...draft, is_paid: e.target.checked })} data-testid="pol-paid" /> Paid policy</label>
                    <div className="flex gap-2">
                        <button onClick={save} className="pill btn-primary px-5 py-2 text-sm" data-testid="pol-save"><Save size={14} className="inline mr-1" /> Save</button>
                        {editingId && <button onClick={() => { setDraft(empty); setEditingId(null); }} className="pill px-5 py-2 text-sm border border-[hsl(var(--border))]">Cancel</button>}
                    </div>
                </div>
            </div>
            <div className="space-y-3">
                {items.map((p) => (
                    <div key={p.id} className="card-clay p-4" data-testid={`admin-pol-${p.id}`}>
                        <div className="flex justify-between">
                            <div className="font-medium">{p.title}</div>
                            <div className="flex gap-2">
                                <button onClick={() => { setEditingId(p.id); setDraft(p); }} className="p-2 hover:bg-[hsl(var(--secondary))] rounded"><Pencil size={14} /></button>
                                <button onClick={() => del(p.id)} className="p-2 hover:bg-[hsl(var(--secondary))] rounded text-[hsl(var(--destructive))]"><Trash2 size={14} /></button>
                            </div>
                        </div>
                        <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{p.is_paid ? `Paid $${p.price}` : "Free"}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function PostsPanel() {
    const [items, setItems] = useState([]);
    const empty = { title: "", body: "", image_url: "", is_announcement: false };
    const [draft, setDraft] = useState(empty);
    const [editingId, setEditingId] = useState(null);
    const { trigger, busy, el } = useUpload();
    const load = () => api.get("/posts").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);
    const save = async () => {
        try {
            if (editingId) await api.put(`/admin/posts/${editingId}`, draft);
            else await api.post("/admin/posts", draft);
            toast.success("Saved"); setDraft(empty); setEditingId(null); load();
        } catch { toast.error("Save failed"); }
    };
    const del = async (id) => { await api.delete(`/admin/posts/${id}`); load(); };
    return (
        <div className="grid lg:grid-cols-2 gap-6">
            {el}
            <div className="card-clay p-6">
                <h3 className="font-display text-xl">{editingId ? "Edit post" : "New post"}</h3>
                <div className="mt-4 space-y-3">
                    <input placeholder="Title" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className="w-full bg-[hsl(var(--input))] rounded-md px-3 py-2 text-sm" data-testid="post-title" />
                    <textarea placeholder="Body" value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} rows={6} className="w-full bg-[hsl(var(--input))] rounded-md px-3 py-2 text-sm" data-testid="post-body" />
                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={draft.is_announcement} onChange={(e) => setDraft({ ...draft, is_announcement: e.target.checked })} data-testid="post-announce" /> Mark as announcement</label>
                    <div className="flex items-center gap-3">
                        {draft.image_url && <img src={draft.image_url} alt="preview" className="w-16 h-16 rounded object-cover" />}
                        <button onClick={() => trigger((url) => setDraft({ ...draft, image_url: url }))} className="pill px-4 py-2 text-sm border border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]" data-testid="post-upload">
                            <Upload size={14} className="inline mr-1" /> {busy ? "Uploading..." : "Upload image"}
                        </button>
                        {draft.image_url && <button onClick={() => setDraft({ ...draft, image_url: "" })} className="text-xs text-[hsl(var(--destructive))]">Remove</button>}
                    </div>
                    <div className="flex gap-2">
                        <button onClick={save} className="pill btn-primary px-5 py-2 text-sm" data-testid="post-save"><Save size={14} className="inline mr-1" /> Save</button>
                        {editingId && <button onClick={() => { setDraft(empty); setEditingId(null); }} className="pill px-5 py-2 text-sm border border-[hsl(var(--border))]">Cancel</button>}
                    </div>
                </div>
            </div>
            <div className="space-y-3">
                {items.map((p) => (
                    <div key={p.id} className="card-clay p-4 flex gap-3" data-testid={`admin-post-${p.id}`}>
                        {p.image_url && <img src={p.image_url} alt="" className="w-16 h-16 rounded object-cover" />}
                        <div className="flex-1">
                            <div className="font-medium">{p.title} {p.is_announcement && <span className="text-[10px] uppercase font-mono bg-[hsl(var(--accent))] text-white px-2 py-0.5 rounded ml-2">Announce</span>}</div>
                            <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1 line-clamp-2">{p.body}</div>
                            <Link to={`/post/${p.id}`} className="text-xs underline-link mt-1 inline-block">View shareable link →</Link>
                        </div>
                        <div className="flex flex-col gap-1">
                            <button onClick={() => { setEditingId(p.id); setDraft(p); }} className="p-2 hover:bg-[hsl(var(--secondary))] rounded"><Pencil size={14} /></button>
                            <button onClick={() => del(p.id)} className="p-2 hover:bg-[hsl(var(--secondary))] rounded text-[hsl(var(--destructive))]"><Trash2 size={14} /></button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function StaffPanel() {
    const [items, setItems] = useState([]);
    const empty = { name: "", role: "", bio: "", photo_url: "", order: 0 };
    const [draft, setDraft] = useState(empty);
    const [editingId, setEditingId] = useState(null);
    const { trigger, busy, el } = useUpload();
    const load = () => api.get("/staff").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);
    const save = async () => {
        try {
            if (editingId) await api.put(`/admin/staff/${editingId}`, draft);
            else await api.post("/admin/staff", draft);
            toast.success("Saved"); setDraft(empty); setEditingId(null); load();
        } catch { toast.error("Save failed"); }
    };
    const del = async (id) => { await api.delete(`/admin/staff/${id}`); load(); };
    return (
        <div className="grid lg:grid-cols-2 gap-6">
            {el}
            <div className="card-clay p-6">
                <h3 className="font-display text-xl">{editingId ? "Edit staff" : "Add staff"}</h3>
                <div className="mt-4 space-y-3">
                    <input placeholder="Name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="w-full bg-[hsl(var(--input))] rounded-md px-3 py-2 text-sm" data-testid="staff-name" />
                    <input placeholder="Role" value={draft.role} onChange={(e) => setDraft({ ...draft, role: e.target.value })} className="w-full bg-[hsl(var(--input))] rounded-md px-3 py-2 text-sm" data-testid="staff-role" />
                    <textarea placeholder="Bio" value={draft.bio} onChange={(e) => setDraft({ ...draft, bio: e.target.value })} rows={4} className="w-full bg-[hsl(var(--input))] rounded-md px-3 py-2 text-sm" data-testid="staff-bio" />
                    <div className="flex items-center gap-3">
                        {draft.photo_url && <img src={draft.photo_url} alt="preview" className="w-16 h-16 rounded-full object-cover" />}
                        <button onClick={() => trigger((url) => setDraft({ ...draft, photo_url: url }))} className="pill px-4 py-2 text-sm border border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]" data-testid="staff-upload">
                            <Upload size={14} className="inline mr-1" /> {busy ? "Uploading..." : "Upload photo"}
                        </button>
                    </div>
                    <input type="number" placeholder="Order" value={draft.order} onChange={(e) => setDraft({ ...draft, order: parseInt(e.target.value || "0") })} className="w-full bg-[hsl(var(--input))] rounded-md px-3 py-2 text-sm" />
                    <div className="flex gap-2">
                        <button onClick={save} className="pill btn-primary px-5 py-2 text-sm" data-testid="staff-save"><Save size={14} className="inline mr-1" /> Save</button>
                        {editingId && <button onClick={() => { setDraft(empty); setEditingId(null); }} className="pill px-5 py-2 text-sm border border-[hsl(var(--border))]">Cancel</button>}
                    </div>
                </div>
            </div>
            <div className="space-y-3">
                {items.map((s) => (
                    <div key={s.id} className="card-clay p-4 flex gap-3" data-testid={`admin-staff-${s.id}`}>
                        {s.photo_url && <img src={s.photo_url} alt="" className="w-14 h-14 rounded-full object-cover" />}
                        <div className="flex-1">
                            <div className="font-medium">{s.name}</div>
                            <div className="text-xs text-[hsl(var(--muted-foreground))]">{s.role}</div>
                        </div>
                        <div className="flex gap-2">
                            <button onClick={() => { setEditingId(s.id); setDraft(s); }} className="p-2 hover:bg-[hsl(var(--secondary))] rounded"><Pencil size={14} /></button>
                            <button onClick={() => del(s.id)} className="p-2 hover:bg-[hsl(var(--secondary))] rounded text-[hsl(var(--destructive))]"><Trash2 size={14} /></button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function AvailabilityPanel() {
    const [av, setAv] = useState(null);
    useEffect(() => { api.get("/availability").then((r) => setAv(r.data)); }, []);
    if (!av) return <div>Loading…</div>;
    const save = async () => { await api.put("/admin/availability", av); toast.success("Availability saved"); };
    const addWindow = (day) => {
        const w = { ...(av.weekly || {}) };
        w[day] = [...(w[day] || []), { start: "09:00", end: "17:00" }];
        setAv({ ...av, weekly: w });
    };
    const updWindow = (day, i, field, value) => {
        const w = { ...(av.weekly || {}) };
        w[day] = w[day].map((x, idx) => idx === i ? { ...x, [field]: value } : x);
        setAv({ ...av, weekly: w });
    };
    const rmWindow = (day, i) => {
        const w = { ...(av.weekly || {}) };
        w[day] = w[day].filter((_, idx) => idx !== i);
        setAv({ ...av, weekly: w });
    };
    const addBlock = () => setAv({ ...av, blocked_dates: [...(av.blocked_dates || []), ""] });
    const updBlock = (i, v) => setAv({ ...av, blocked_dates: av.blocked_dates.map((d, idx) => idx === i ? v : d) });
    const rmBlock = (i) => setAv({ ...av, blocked_dates: av.blocked_dates.filter((_, idx) => idx !== i) });

    return (
        <div className="grid lg:grid-cols-2 gap-6">
            <div className="card-clay p-6">
                <h3 className="font-display text-xl">Weekly hours</h3>
                <div className="mt-4 flex gap-2 items-center text-sm">
                    <label>Slot length (min)</label>
                    <input type="number" value={av.slot_minutes} onChange={(e) => setAv({ ...av, slot_minutes: parseInt(e.target.value || "30") })} className="w-24 bg-[hsl(var(--input))] rounded-md px-2 py-1" data-testid="slot-minutes" />
                </div>
                <div className="mt-4 space-y-4">
                    {DAYS.map((day) => (
                        <div key={day} data-testid={`day-${day}`}>
                            <div className="flex justify-between items-center">
                                <div className="capitalize font-medium text-sm">{day}</div>
                                <button onClick={() => addWindow(day)} className="text-xs underline-link" data-testid={`add-window-${day}`}>+ add window</button>
                            </div>
                            <div className="mt-2 space-y-2">
                                {(av.weekly?.[day] || []).map((w, i) => (
                                    <div key={i} className="flex gap-2 items-center">
                                        <input type="time" value={w.start} onChange={(e) => updWindow(day, i, "start", e.target.value)} className="bg-[hsl(var(--input))] rounded px-2 py-1 text-sm" />
                                        <span>→</span>
                                        <input type="time" value={w.end} onChange={(e) => updWindow(day, i, "end", e.target.value)} className="bg-[hsl(var(--input))] rounded px-2 py-1 text-sm" />
                                        <button onClick={() => rmWindow(day, i)} className="text-[hsl(var(--destructive))]"><Trash2 size={14} /></button>
                                    </div>
                                ))}
                                {(av.weekly?.[day] || []).length === 0 && <div className="text-xs text-[hsl(var(--muted-foreground))]">closed</div>}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            <div className="card-clay p-6">
                <h3 className="font-display text-xl">Blocked dates</h3>
                <div className="mt-4 space-y-2">
                    {(av.blocked_dates || []).map((d, i) => (
                        <div key={i} className="flex gap-2 items-center">
                            <input type="date" value={d} onChange={(e) => updBlock(i, e.target.value)} className="bg-[hsl(var(--input))] rounded px-2 py-1 text-sm" />
                            <button onClick={() => rmBlock(i)} className="text-[hsl(var(--destructive))]"><Trash2 size={14} /></button>
                        </div>
                    ))}
                    <button onClick={addBlock} className="text-xs underline-link" data-testid="add-blocked-date">+ block a date</button>
                </div>
                <button onClick={save} className="pill btn-primary px-5 py-2 text-sm mt-8" data-testid="save-availability"><Save size={14} className="inline mr-1" /> Save availability</button>
            </div>
        </div>
    );
}

function AppointmentsPanel() {
    const [items, setItems] = useState([]);
    const load = () => api.get("/admin/appointments").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);
    const setStatus = async (id, status) => {
        await api.put(`/admin/appointments/${id}/status`, null, { params: { status } });
        load();
    };
    const del = async (id) => { await api.delete(`/admin/appointments/${id}`); load(); };
    return (
        <div className="card-clay overflow-x-auto">
            <table className="w-full text-sm">
                <thead className="bg-[hsl(var(--secondary))]">
                    <tr className="text-left"><th className="p-3">When</th><th className="p-3">Client</th><th className="p-3">Service</th><th className="p-3">Mode</th><th className="p-3">Link</th><th className="p-3">Status</th><th className="p-3">Actions</th></tr>
                </thead>
                <tbody>
                    {items.map((a) => (
                        <tr key={a.id} className="border-t border-[hsl(var(--border))]" data-testid={`appt-${a.id}`}>
                            <td className="p-3 font-mono text-xs">{a.date} {a.time}</td>
                            <td className="p-3">{a.name}<br /><span className="text-[hsl(var(--muted-foreground))] text-xs">{a.email}{a.phone && ` · ${a.phone}`}</span></td>
                            <td className="p-3">{a.service_title}</td>
                            <td className="p-3"><span className={`text-xs font-mono uppercase px-2 py-1 rounded ${a.mode === "online" ? "bg-[hsl(var(--accent))] text-white" : "bg-[hsl(var(--secondary))]"}`}>{a.mode === "online" ? "Video" : "In person"}</span></td>
                            <td className="p-3">{a.meeting_url ? <a href={a.meeting_url} target="_blank" rel="noopener noreferrer" className="text-xs underline text-[hsl(var(--primary))] break-all" data-testid={`meeting-link-${a.id}`}>Open</a> : <span className="text-xs text-[hsl(var(--muted-foreground))]">—</span>}</td>
                            <td className="p-3"><span className="text-xs font-mono uppercase">{a.status}</span></td>
                            <td className="p-3">
                                <div className="flex gap-1">
                                    <button onClick={() => setStatus(a.id, "confirmed")} title="Confirm" className="p-1 hover:bg-[hsl(var(--secondary))] rounded text-[hsl(var(--success))]" data-testid={`confirm-${a.id}`}><Check size={14} /></button>
                                    <button onClick={() => setStatus(a.id, "cancelled")} title="Cancel" className="p-1 hover:bg-[hsl(var(--secondary))] rounded text-[hsl(var(--destructive))]"><X size={14} /></button>
                                    <button onClick={() => del(a.id)} title="Delete" className="p-1 hover:bg-[hsl(var(--secondary))] rounded"><Trash2 size={14} /></button>
                                </div>
                            </td>
                        </tr>
                    ))}
                    {items.length === 0 && <tr><td colSpan="7" className="p-6 text-center text-[hsl(var(--muted-foreground))]">No appointments yet.</td></tr>}
                </tbody>
            </table>
        </div>
    );
}

function SettingsPanel() {
    const [s, setS] = useState(null);
    const [testingZoom, setTestingZoom] = useState(false);
    const [showZoomHelp, setShowZoomHelp] = useState(false);
    const [stripeInfo, setStripeInfo] = useState(null);
    const { trigger, busy, el } = useUpload();
    useEffect(() => {
        api.get("/admin/settings").then((r) => setS(r.data));
        api.get("/admin/payments/onboarding").then((r) => setStripeInfo(r.data));
    }, []);
    if (!s) return null;
    const save = async () => { await api.put("/admin/settings", s); toast.success("Settings saved"); };
    const testZoom = async () => {
        setTestingZoom(true);
        try {
            await api.put("/admin/settings", s);
            await api.post("/admin/settings/zoom/test");
            toast.success("Zoom connected — meetings will use your Zoom account");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Zoom test failed — check credentials & scopes");
        } finally {
            setTestingZoom(false);
        }
    };
    const zoomReady = !!(s.zoom_account_id && s.zoom_client_id && s.zoom_client_secret);
    return (
        <div className="space-y-6 max-w-3xl">
            <div className="card-clay p-6">
                {el}
                <h3 className="font-display text-xl">Business settings</h3>
                <div className="mt-4 space-y-3">
                    <label className="block text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))] font-mono">Business name</label>
                    <input value={s.business_name || ""} onChange={(e) => setS({ ...s, business_name: e.target.value })} className="w-full bg-[hsl(var(--input))] rounded-md px-3 py-2 text-sm" data-testid="settings-name" />
                    <label className="block text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))] font-mono mt-2">Tagline</label>
                    <input value={s.tagline || ""} onChange={(e) => setS({ ...s, tagline: e.target.value })} className="w-full bg-[hsl(var(--input))] rounded-md px-3 py-2 text-sm" />
                    <label className="block text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))] font-mono mt-2">About</label>
                    <textarea rows={4} value={s.about || ""} onChange={(e) => setS({ ...s, about: e.target.value })} className="w-full bg-[hsl(var(--input))] rounded-md px-3 py-2 text-sm" />
                    <div className="grid md:grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))] font-mono">Contact email</label>
                            <input value={s.contact_email || ""} onChange={(e) => setS({ ...s, contact_email: e.target.value })} className="w-full bg-[hsl(var(--input))] rounded-md px-3 py-2 text-sm" />
                        </div>
                        <div>
                            <label className="block text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))] font-mono">Phone</label>
                            <input value={s.contact_phone || ""} onChange={(e) => setS({ ...s, contact_phone: e.target.value })} className="w-full bg-[hsl(var(--input))] rounded-md px-3 py-2 text-sm" />
                        </div>
                    </div>
                    <label className="block text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))] font-mono mt-2">Address</label>
                    <input value={s.address || ""} onChange={(e) => setS({ ...s, address: e.target.value })} className="w-full bg-[hsl(var(--input))] rounded-md px-3 py-2 text-sm" />

                    <div className="grid md:grid-cols-2 gap-6 mt-4">
                        <div>
                            <div className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))] font-mono">Hero image</div>
                            {s.hero_image_url && <img src={s.hero_image_url} alt="hero" className="mt-2 w-full h-32 object-cover rounded" />}
                            <button onClick={() => trigger((url) => setS({ ...s, hero_image_url: url }))} className="mt-2 pill px-4 py-2 text-sm border border-[hsl(var(--border))]" data-testid="upload-hero">
                                <Upload size={14} className="inline mr-1" /> {busy ? "Uploading..." : "Upload hero image"}
                            </button>
                        </div>
                        <div>
                            <div className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))] font-mono">Logo</div>
                            {s.logo_url && <img src={s.logo_url} alt="logo" className="mt-2 w-20 h-20 object-cover rounded-full" />}
                            <button onClick={() => trigger((url) => setS({ ...s, logo_url: url }))} className="mt-2 pill px-4 py-2 text-sm border border-[hsl(var(--border))]" data-testid="upload-logo">
                                <Upload size={14} className="inline mr-1" /> {busy ? "Uploading..." : "Upload logo"}
                            </button>
                        </div>
                    </div>

                    <button onClick={save} className="pill btn-primary px-6 py-2 text-sm mt-4" data-testid="save-settings"><Save size={14} className="inline mr-1" /> Save settings</button>
                </div>
            </div>

            {/* Stripe card payments */}
            <div className="card-clay p-6" data-testid="stripe-section">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <div className="flex items-center gap-2">
                            <h3 className="font-display text-xl">Card payments (Stripe)</h3>
                            <span className={`chip ${stripeInfo?.enabled ? "!bg-[hsl(var(--success))] !text-white" : "!bg-[#F3E7D0] !text-[#8A6A2E]"}`} data-testid="stripe-status">
                                {stripeInfo?.enabled ? `${stripeInfo?.mode === "live" ? "Live" : "Test mode"}` : "Not configured"}
                            </span>
                        </div>
                        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 max-w-lg">
                            {stripeInfo?.enabled
                                ? (stripeInfo?.mode === "live"
                                    ? "Card payments are live — customers can pay by card on the booking page."
                                    : "Card payments are ready in test mode. Claim your Stripe account to receive real money.")
                                : "Card payments are turned off — your booking page uses the manual payout methods above."}
                        </p>
                    </div>
                </div>

                {stripeInfo?.onboarding_url && (
                    <div className="mt-5 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--secondary))]/40 p-5">
                        <div className="font-mono text-[11px] uppercase tracking-widest text-[hsl(var(--muted-foreground))]">One-time setup</div>
                        <p className="text-sm mt-2">Click below to claim your Stripe account. Add your business details, verify your identity, and Stripe will send real payouts to your bank once approved.</p>
                        <a
                            href={stripeInfo.onboarding_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-3 inline-block pill btn-primary px-5 py-2 text-sm"
                            data-testid="stripe-claim-btn"
                        >
                            Claim my Stripe account →
                        </a>
                        <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-3">
                            Until claim is complete you can still test the flow with card number <span className="font-mono">4242 4242 4242 4242</span>, any future expiry, any CVC.
                        </p>
                    </div>
                )}

                <div className="mt-5 text-xs text-[hsl(var(--muted-foreground))]">
                    Tax handling: Stripe charges the right tax at checkout; you file the returns yourself (+0.5% per transaction). You can change this later — just ask.
                </div>
            </div>

            {/* Zoom card — separated for clarity */}
            <div className="card-clay p-6" data-testid="zoom-section">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <div className="flex items-center gap-2">
                            <h3 className="font-display text-xl">Zoom account</h3>
                            <span className={`chip ${zoomReady ? "!bg-[hsl(var(--success))] !text-white" : "!bg-[#F3E7D0] !text-[#8A6A2E]"}`} data-testid="zoom-status">
                                {zoomReady ? "Connected" : "Not connected"}
                            </span>
                        </div>
                        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 max-w-lg">
                            {zoomReady
                                ? "Online bookings create real Zoom meetings in your account."
                                : "Right now, online bookings use a free Jitsi Meet room (works instantly, no setup). Connect Zoom below to use your own account instead — one-time setup, then automatic forever."}
                        </p>
                    </div>
                </div>

                <div className="mt-6 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--secondary))]/40 p-5">
                    <div className="font-mono text-[11px] uppercase tracking-widest text-[hsl(var(--muted-foreground))]">One-time setup — 3 minutes</div>
                    <ol className="mt-3 space-y-3 text-sm">
                        <li className="flex gap-3">
                            <span className="flex-none w-6 h-6 rounded-full bg-[hsl(var(--primary))] text-white grid place-items-center text-xs font-mono">1</span>
                            <div>
                                Open Zoom's app builder:{" "}
                                <a href="https://marketplace.zoom.us/develop/create" target="_blank" rel="noopener noreferrer" className="underline text-[hsl(var(--primary))] font-medium" data-testid="zoom-marketplace-link">
                                    marketplace.zoom.us/develop/create
                                </a>
                                <span className="block text-xs text-[hsl(var(--muted-foreground))] mt-1">Sign in with the same Google/Gmail you use for Zoom.</span>
                            </div>
                        </li>
                        <li className="flex gap-3">
                            <span className="flex-none w-6 h-6 rounded-full bg-[hsl(var(--primary))] text-white grid place-items-center text-xs font-mono">2</span>
                            <div>
                                Choose <strong>Server-to-Server OAuth</strong> → <strong>Create</strong>. Name it anything (e.g. "Booking site").
                            </div>
                        </li>
                        <li className="flex gap-3">
                            <span className="flex-none w-6 h-6 rounded-full bg-[hsl(var(--primary))] text-white grid place-items-center text-xs font-mono">3</span>
                            <div>
                                On the <strong>App Credentials</strong> tab, copy <strong>Account ID</strong>, <strong>Client ID</strong>, and <strong>Client Secret</strong> — paste them below.
                            </div>
                        </li>
                        <li className="flex gap-3">
                            <span className="flex-none w-6 h-6 rounded-full bg-[hsl(var(--primary))] text-white grid place-items-center text-xs font-mono">4</span>
                            <div>
                                On the <strong>Scopes</strong> tab, add <span className="font-mono text-xs bg-white px-1.5 py-0.5 rounded border border-[hsl(var(--border))]">meeting:write:meeting</span>, then click <strong>Activation → Activate</strong>.
                            </div>
                        </li>
                        <li className="flex gap-3">
                            <span className="flex-none w-6 h-6 rounded-full bg-[hsl(var(--primary))] text-white grid place-items-center text-xs font-mono">5</span>
                            <div>Come back here, hit <strong>Save & test connection</strong> — done forever.</div>
                        </li>
                    </ol>
                    <button onClick={() => setShowZoomHelp((v) => !v)} className="mt-3 text-xs underline text-[hsl(var(--primary))]" data-testid="zoom-help-toggle">
                        {showZoomHelp ? "Hide details" : "Why is this needed?"}
                    </button>
                    {showZoomHelp && (
                        <p className="mt-2 text-xs text-[hsl(var(--muted-foreground))] leading-relaxed">
                            Zoom's API requires every external app to identify itself with credentials from your Zoom account. "Sign in with Google" logs you into Zoom's website but doesn't grant other apps permission to create meetings. This one-time step gives us the tokens we need to make meetings automatically in <em>your</em> Zoom, appearing under your account, with your branding and settings.
                        </p>
                    )}
                </div>

                <div className="grid md:grid-cols-3 gap-3 mt-6">
                    <div>
                        <label className="block text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))] font-mono">Account ID</label>
                        <input value={s.zoom_account_id || ""} onChange={(e) => setS({ ...s, zoom_account_id: e.target.value })} className="w-full bg-[hsl(var(--input))] rounded-md px-3 py-2 text-sm mt-1 font-mono" data-testid="zoom-account-id" autoComplete="off" placeholder="Paste Account ID" />
                    </div>
                    <div>
                        <label className="block text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))] font-mono">Client ID</label>
                        <input value={s.zoom_client_id || ""} onChange={(e) => setS({ ...s, zoom_client_id: e.target.value })} className="w-full bg-[hsl(var(--input))] rounded-md px-3 py-2 text-sm mt-1 font-mono" data-testid="zoom-client-id" autoComplete="off" placeholder="Paste Client ID" />
                    </div>
                    <div>
                        <label className="block text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))] font-mono">Client Secret</label>
                        <input type="password" value={s.zoom_client_secret || ""} onChange={(e) => setS({ ...s, zoom_client_secret: e.target.value })} className="w-full bg-[hsl(var(--input))] rounded-md px-3 py-2 text-sm mt-1 font-mono" data-testid="zoom-client-secret" autoComplete="off" placeholder="Paste Client Secret" />
                    </div>
                </div>
                <div className="mt-4 flex gap-2 flex-wrap items-center">
                    <button onClick={testZoom} disabled={testingZoom || !s.zoom_account_id || !s.zoom_client_id || !s.zoom_client_secret} className="pill btn-primary px-5 py-2 text-sm disabled:opacity-50" data-testid="zoom-test-btn">
                        {testingZoom ? "Connecting..." : (zoomReady ? "Re-test connection" : "Save & test connection")}
                    </button>
                    {zoomReady && (
                        <button
                            onClick={() => { setS({ ...s, zoom_account_id: "", zoom_client_id: "", zoom_client_secret: "" }); }}
                            className="pill px-4 py-2 text-sm text-[hsl(var(--destructive))] hover:bg-[hsl(var(--secondary))]"
                            data-testid="zoom-disconnect-btn"
                        >
                            Clear (revert to Jitsi)
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

/* -------------------- Shell -------------------- */
const TABS = [
    { key: "appointments", label: "Appointments", icon: ClipboardList, Component: AppointmentsPanel },
    { key: "services", label: "Services", icon: FileText, Component: ServicesPanel },
    { key: "availability", label: "Availability", icon: CalIcon, Component: AvailabilityPanel },
    { key: "posts", label: "Posts", icon: Megaphone, Component: PostsPanel },
    { key: "policies", label: "Policies", icon: ShieldCheck, Component: PoliciesPanel },
    { key: "staff", label: "Staff", icon: Users, Component: StaffPanel },
    { key: "payments", label: "Payments", icon: Wallet, Component: PayoutsPanel },
    { key: "settings", label: "Settings", icon: SettingsIcon, Component: SettingsPanel },
];

export default function AdminDashboard() {
    const { verified, logout } = useAdminAuth();
    const navigate = useNavigate();
    const [tab, setTab] = useState("appointments");

    useEffect(() => {
        if (!verified && !localStorage.getItem("kabir_admin_token")) navigate("/admin", { replace: true });
    }, [verified, navigate]);

    const Active = TABS.find((t) => t.key === tab)?.Component || (() => null);

    return (
        <div className="min-h-screen flex" style={{ backgroundColor: "#FAF9F5" }} data-testid="admin-dashboard">
            <Toaster position="top-right" />
            <aside className="w-64 hidden md:flex flex-col p-6 border-r border-[hsl(var(--border))] bg-[hsl(var(--background))]">
                <Link to="/" className="font-display text-xl">Admin</Link>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))] mt-1">Control center</div>
                <nav className="mt-8 flex-1 space-y-1">
                    {TABS.map((t) => {
                        const Icon = t.icon;
                        return (
                            <button
                                key={t.key}
                                onClick={() => setTab(t.key)}
                                data-testid={`admin-tab-${t.key}`}
                                className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center gap-2 transition-colors ${
                                    tab === t.key ? "bg-[hsl(var(--primary))] text-white" : "hover:bg-[hsl(var(--secondary))]"
                                }`}
                            >
                                <Icon size={14} /> {t.label}
                            </button>
                        );
                    })}
                </nav>
                <button onClick={() => { logout(); navigate("/admin"); }} className="mt-8 pill px-4 py-2 text-sm border border-[hsl(var(--border))] flex items-center gap-2 justify-center" data-testid="admin-logout">
                    <LogOut size={14} /> Sign out
                </button>
            </aside>

            <main className="flex-1 p-6 md:p-10 overflow-x-hidden">
                <div className="md:hidden mb-6 flex gap-2 overflow-x-auto pb-2">
                    {TABS.map((t) => (
                        <button key={t.key} onClick={() => setTab(t.key)} className={`px-3 py-1 rounded-full text-xs whitespace-nowrap ${tab === t.key ? "bg-[hsl(var(--primary))] text-white" : "bg-[hsl(var(--secondary))]"}`}>{t.label}</button>
                    ))}
                </div>
                <h1 className="font-display text-3xl capitalize" data-testid="admin-page-title">{tab}</h1>
                <div className="mt-8">
                    <Active />
                </div>
            </main>
        </div>
    );
}
